'use client';

/**
 * Netflix "continue watching" hover-zoom tile, ported from the legacy
 * LMS's CourseCardZoom and restyled after the front/back hover-swap
 * card at https://codepen.io/canumay/pen/ExWvpdv: a plain poster sits
 * in normal flow (so it sets the grid cell's height); on hover a
 * "back" panel — cover + action row + title + description + progress —
 * appears zoomed over the poster's own spot.
 *
 * <p>The back panel is {@code position: fixed} at viewport coordinates
 * measured from the front poster's own {@code getBoundingClientRect()}
 * on hover-enter — the same trick {@code UdemyCourseCard}'s popover
 * uses — precisely so it is never clipped by an ancestor's
 * {@code overflow} (a horizontally-scrolling rail needs
 * {@code overflow-x} set to something other than {@code visible}, and
 * a grid/rail wrapper commonly needs {@code overflow} clipping too; a
 * {@code position: fixed} element is the one thing guaranteed to
 * escape that in every browser, no CSS workaround required).
 *
 * <p>Unlike the codepen (a static demo with no real links), the whole
 * card still has to navigate — the cover and title are themselves
 * links — so the "Watch later" / "Favorites" action row sits OUTSIDE
 * any anchor (nesting a `<button>` inside an `<a>` is invalid HTML and
 * behaves inconsistently across browsers).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, BookmarkCheck, Clock, Heart, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PortalLink, useCurrentUser, useUserResourceSource } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { LevelBadge } from '../_shared/LevelBadge';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { Button, Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';
import { isEmbedUrl, PreviewEmbed, PreviewVideo } from '../_shared/CoursePreviewMedia';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

const ZOOM_CARD_CSS = `
.lms-zoom-front {
  display: block;
  border-radius: var(--radius-lg, 10px);
  overflow: hidden;
  box-shadow: var(--shadow-xs, 0 1px 2px rgba(0,0,0,0.08));
  transition: opacity 160ms ease-out;
}
.lms-zoom-front[data-dimmed="true"] { opacity: 0; }
.lms-zoom-back {
  border-radius: var(--radius-lg, 10px);
  overflow: hidden;
  background: var(--color-surface-card, #fff);
  border: 1px solid var(--color-border-default, #e0e0e0);
  box-shadow: var(--shadow-lg, 0 14px 32px rgba(0,0,0,0.24));
  /* Top-anchored vertically, not center: growing from the vertical center
     pushes the panel both up AND down, which reads as "floating between
     rows" rather than "this card, only bigger". Anchoring to the top
     edge keeps it flush with the row's own top line — it only grows
     down, clearly still attached to its own column. Horizontal origin
     (left/right/center) is set inline per-instance — see onEnter's
     room check, same idea as the 'udemy' popover picking a side with
     enough clearance — so a card flush against the viewport's left or
     right edge only grows toward the side with room instead of bleeding
     off-screen. Transitions don't run on first paint, so this animates
     in via @keyframes instead (same reason the 'udemy' popover uses
     one). */
  animation: lms-zoom-in 200ms ease-out forwards;
}
@keyframes lms-zoom-in {
  from { opacity: 0; transform: scale(1) translateY(0); }
  to   { opacity: 1; transform: scale(1.2) translateY(-8%); }
}
.lms-zoom-action-btn:not([disabled]):hover,
.lms-zoom-action-btn[aria-pressed="true"] {
  background: var(--color-brand-500, #3b5bdb) !important;
  border-color: var(--color-brand-500, #3b5bdb) !important;
  color: var(--color-text-on-brand, #fff) !important;
}
.lms-zoom-back-title-link {
  color: var(--color-text-primary, #1c1d1f);
  text-decoration: none !important ;
}
.lms-zoom-back-title-link:hover {
  text-decoration: underline;
}
@media (prefers-reduced-motion: reduce) {
  .lms-zoom-front { transition: none; }
  .lms-zoom-back { animation: none; opacity: 1; transform: scale(1.2) translateY(-8%); }
}
`;

// Matches the `scale(1.2)` in the keyframes above — used to estimate how
// much extra width the zoom needs on each side before picking a growth
// direction (see `onEnter`).
const ZOOM_SCALE = 1.2;

/** "2.1h / 4h" when the numeric total is known; otherwise just the
 *  rounded percentage — same fallback every other card variant uses
 *  when a course only carries a formatted `duration` string. */
function formatProgressLabel(progress: number, totalHours?: number): string {
  if (typeof totalHours === 'number' && totalHours > 0) {
    const elapsed = (progress / 100) * totalHours;
    const fmt = (h: number) => (Number.isInteger(h) ? `${h}` : h.toFixed(1));
    return `${fmt(elapsed)} / ${fmt(totalHours)}h`;
  }
  return `${Math.round(progress)}%`;
}

/** Duration label for the front poster's meta row: prefers the
 *  pre-formatted string, falls back to "{n}h" from totalHours. */
function formatDuration(duration?: string, totalHours?: number): string | null {
  if (duration) return duration;
  if (typeof totalHours === 'number' && totalHours > 0) return `${totalHours}h`;
  return null;
}

/**
 * "Add to favorites" — wired to {@code UserResourceSource.wishlist}
 * (course-level save-for-later; the only per-course "saved list"
 * concept this host contract exposes). Mirrors {@code WatchLaterButton}'s
 * optimistic-toggle/error-rollback shape; success/error feedback goes
 * through {@code sonner}'s {@code toast} (already a package dependency,
 * rendered by the host's mounted {@code <Toaster />|<Sonner />}), reusing
 * the exact copy the LMS already ships for this action
 * ({@code message.addedToFavoriteCourseList} etc.).
 */
function useFavoriteToggle(courseSlug: string, courseTitle: string) {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.wishlist) return null;
      return (await ur.wishlist.has(courseSlug, signal)) ?? false;
    },
    [user, ur, courseSlug],
  );

  const available = Boolean(user) && Boolean(ur?.wishlist);
  const favorited = optimistic ?? (state.kind === 'loaded' ? state.data : false);

  const toggle = async () => {
    if (!available || toggling) return;
    setToggling(true);
    setOptimistic(!favorited);
    try {
      const res = await ur!.wishlist!.toggle({ courseSlug });
      if (!res || res.skipped) {
        setOptimistic(null);
      } else {
        const active = Boolean(res.active);
        setOptimistic(active);
        toast.success(
          t(active ? 'message.addedToFavoriteCourseList' : 'message.removedFromFavoriteCourseList'),
          { description: courseTitle },
        );
      }
    } catch (err) {
      setOptimistic(null);
      console.error('[ClassicNetflixZoomCourseCard] wishlist toggle failed', err);
      toast.error(t('label.error', 'Failed — try again'));
    } finally {
      setToggling(false);
    }
  };

  return { available, favorited, toggling, toggle };
}

/**
 * "Watch later" — wired to {@code UserResourceSource.courseWatchLater}
 * (the LMS backend's course-scoped watch-later bookmark, distinct from
 * Cinema8's video-queue {@code watchLater} namespace). Same
 * optimistic-toggle/error-rollback shape as {@code useFavoriteToggle}
 * above — it's the same kind of per-course bookmark, just a different
 * type — including the {@code sonner} toast feedback.
 */
function useWatchLaterToggle(courseSlug: string, courseTitle: string) {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.courseWatchLater) return null;
      return (await ur.courseWatchLater.has(courseSlug, signal)) ?? false;
    },
    [user, ur, courseSlug],
  );

  const available = Boolean(user) && Boolean(ur?.courseWatchLater);
  const active = optimistic ?? (state.kind === 'loaded' ? state.data : false);

  const toggle = async () => {
    if (!available || toggling) return;
    setToggling(true);
    setOptimistic(!active);
    try {
      const res = await ur!.courseWatchLater!.toggle({ courseSlug });
      if (!res || res.skipped) {
        setOptimistic(null);
      } else {
        const next = Boolean(res.active);
        setOptimistic(next);
        toast.success(
          t(next ? 'message.addedToWatchLaterList' : 'message.removedFromWatchLaterList'),
          { description: courseTitle },
        );
      }
    } catch (err) {
      setOptimistic(null);
      console.error('[ClassicNetflixZoomCourseCard] watch-later toggle failed', err);
      toast.error(t('label.error', 'Failed — try again'));
    } finally {
      setToggling(false);
    }
  };

  return { available, active, toggling, toggle };
}

export function ClassicNetflixZoomCourseCard({
  course: c,
  autoPlayPreview = false,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = true,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  /** When a preview clip exists, play it (muted, looped) in place of the
   *  static cover while the card is zoomed on hover. Mirrors the 'udemy'
   *  card's autoplay option. Defaults to off — the cover stays static. */
  autoPlayPreview?: boolean;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [hovered, setHovered] = useState(false);
  // Viewport-space coordinates for the back panel, measured from the front
  // poster's own rect on hover-enter — see the file-level comment for why
  // this has to be `position: fixed` instead of `absolute`. `originX` is
  // the horizontal `transform-origin` to grow from: '0%' (left-anchored,
  // grows only rightward) when there isn't room to spare on the left,
  // '100%' (right-anchored) when there isn't room on the right, else
  // '50%' (grows evenly both ways) — same room check UdemyCourseCard's
  // popover does before picking a side.
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
    originX: string;
  } | null>(null);
  const { t } = useTranslation('common');
  const favorite = useFavoriteToggle(c.id, c.title);
  const watchLater = useWatchLaterToggle(c.id, c.title);

  const onEnter = () => {
    clearTimeout(leaveTimer.current);
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Centered growth (the default) needs half the scale delta free on
    // EACH side. If one side is too tight, anchoring to the opposite
    // edge instead needs the FULL delta on the other side — only worth
    // it if that side actually has the room.
    const fullDelta = rect.width * (ZOOM_SCALE - 1);
    const halfDelta = fullDelta / 2;
    const roomLeft = rect.left;
    const roomRight = window.innerWidth - rect.right;
    const originX =
      roomLeft < halfDelta && roomRight >= fullDelta
        ? '0%'
        : roomRight < halfDelta && roomLeft >= fullDelta
          ? '100%'
          : '50%';
    setCoords({ top: rect.top, left: rect.left, width: rect.width, originX });
    setHovered(true);
  };

  const onLeave = () => {
    leaveTimer.current = setTimeout(() => setHovered(false), 100);
  };

  // Fixed positioning is viewport-relative, so it won't track the card if
  // the page (or an ancestor rail) scrolls while hovered — close instead of
  // drifting. `capture: true` catches scroll events fired on the rail's own
  // scroll container too, since 'scroll' doesn't bubble.
  useEffect(() => {
    if (!hovered) return;
    const close = () => setHovered(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [hovered]);

  // Used only for the video poster below — the cover itself renders via
  // <CourseCoverImage>, which resizes/fallbacks internally.
  const imageUrl = typeof c.image === 'string' ? resizeImageUrl(c.image, '480x0') : undefined;
  const hasVideo = Boolean(c.previewVideoUrl);
  // Mounting the <video>/<iframe> only while hovered matches the 'udemy'
  // card's rule: mounted === playing, so playback stops the instant the
  // card un-zooms instead of looping silently offscreen.
  const playVideo = hovered && hasVideo && autoPlayPreview;
  const hasProgress = showProgress && typeof c.progress === 'number'; //&& c.progress > 0;
  const durationLabel = formatDuration(c.duration, c.totalHours);

  const cover = playVideo ? (
    isEmbedUrl(c.previewVideoUrl!) ? (
      <PreviewEmbed src={c.previewVideoUrl!} title={c.title} poster={imageUrl} />
    ) : (
      <PreviewVideo src={c.previewVideoUrl!} poster={imageUrl} />
    )
  ) : (
    <CourseCoverImage image={typeof c.image === 'string' ? c.image : undefined} resizeTo="480x0" />
  );

  return (
    <div
      ref={cardRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      style={{ position: 'relative', height: '100%' }}
    >
      <style dangerouslySetInnerHTML={{ __html: ZOOM_CARD_CSS }} />

      {/* Front — resting poster, sets the grid cell's height. At a glance
          it's the cover plus a level + duration meta row — no title (that
          lives in the zoomed panel now); actions/description/progress
          only show up zoomed. Stays in the DOM (just dimmed) while
          hovered, so the layout never loses its grid cell. */}
      <Card asChild>
        <PortalLink
          href={c.href}
          className="lms-zoom-front"
          data-dimmed={hovered ? 'true' : undefined}
          style={{ ...cardReset, position: 'relative', overflow: 'hidden' }}
        >
          <div
            style={{
              position: 'relative',
              aspectRatio: 'var(--app-course-image-aspect-ratio, 1.5)',
            }}
          >
            {cover}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'var(--color-overlay-scrim, linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.32) 35%, rgba(0,0,0,0) 60%))',
              }}
            />
            <h3
              style={{
                position: 'absolute',
                left: 10,
                right: 10,
                bottom: 8,
                margin: 0,
                fontSize: 'var(--font-size-sm, 13px)',
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'var(--color-text-primary-inverse, #e4e4e7)',
                textShadow: 'var(--text-shadow-scrim, 0 1px 3px rgba(0,0,0,0.5))',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {c.title}
            </h3>
          </div>
        </PortalLink>
      </Card>

      {/* Back — cover + action row + title + description + progress.
          `position: fixed` at the front poster's own viewport rect (see
          `coords`), so it paints on top of the page and is never clipped
          by an ancestor's `overflow` (e.g. the course rail's horizontal
          scroll container, or a grid wrapper). Not itself a link: the
          action buttons need to sit outside any anchor (a <button>
          nested in an <a> is invalid HTML), so the cover and title are
          each their own PortalLink instead. */}
      {hovered && coords && (
        <div
          className="lms-zoom-back"
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            transformOrigin: `${coords.originX} 0%`,
            zIndex: 9999,
          }}
        >
          <PortalLink href={c.href} style={{ position: 'relative', display: 'block' }}>
            <div
              style={{
                position: 'relative',
                aspectRatio: 'var(--app-course-image-aspect-ratio, 1.5)',
              }}
            >
              {cover}
            </div>
          </PortalLink>

          <div style={{ padding: '10px 10px 14px' }}>
            {/* The level badge always renders (missing level → "Genel"), so the
                meta row is always present. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 8,
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 600,
                color: 'var(--color-text-tertiary, #6a6f73)',
              }}
            >
              <LevelBadge
                level={c.level}
                variant="solid"
                style={{
                  borderRadius: '8px',
                  padding: 'var(--space-1, 8px) var(--space-2, 16px)',
                  fontSize: 'var(--font-size-xs, 14px)',
                }}
              />
              {typeof c.enrolled === 'number' && c.enrolled > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Users size={13} />
                  {c.enrolled.toLocaleString()}
                </span>
              )}
              {durationLabel && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={13} />
                  {durationLabel}
                </span>
              )}
            </div>

            <h4
              style={{
                margin: 0,
                fontSize: 'var(--font-size-md, 15px)',
                fontWeight: 600,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              <PortalLink href={c.href} className="lms-zoom-back-title-link">
                {c.title}
              </PortalLink>
            </h4>

            {c.description && (
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: 'var(--font-size-xs, 12px)',
                  lineHeight: 1.4,
                  color: 'var(--color-text-tertiary, #6a6f73)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {c.description}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                radius="pill"
                className="lms-zoom-action-btn"
                title={t('label.watchLater')}
                aria-label={t('label.watchLater')}
                aria-pressed={watchLater.active}
                disabled={!watchLater.available || watchLater.toggling}
                onClick={(e) => {
                  e.preventDefault();
                  void watchLater.toggle();
                }}
              >
                {watchLater.active ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                radius="pill"
                className="lms-zoom-action-btn"
                title={t('title.favorites')}
                aria-label={t('title.favorites')}
                aria-pressed={favorite.favorited}
                disabled={!favorite.available || favorite.toggling}
                onClick={(e) => {
                  e.preventDefault();
                  void favorite.toggle();
                }}
              >
                <Heart size={16} fill={favorite.favorited ? 'currentColor' : 'none'} />
              </Button>
            </div>

            {hasProgress && (
              <div style={{ marginTop: 10 }}>
                <CourseProgressBar
                  progress={c.progress!}
                  height={3}
                  showText={showProgressText && progressTextPlace.startsWith('inside')}
                  textPlace={progressTextPlace}
                />
                {showProgressText && !progressTextPlace.startsWith('inside') && (
                  <div
                    style={{
                      marginTop: 4,
                      textAlign: progressTextPlace === 'outside-left' ? 'left' : 'right',
                      fontSize: 'var(--font-size-xs, 11px)',
                      fontWeight: 700,
                      color: 'var(--color-text-secondary, #4a4a4a)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatProgressLabel(c.progress!, c.totalHours)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

ClassicNetflixZoomCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 220,
  gap: 'clamp(20px, 3vw, 34px)',
} satisfies CardGridSpec;
